import { NavLink, useLocation, useNavigate } from "react-router-dom";
import type { CSSProperties } from "react";
import { useIsMobile } from "../../lib/useIsMobile";

export type OperationsStudioTab =
  | "dashboard"
  | "context"
  | "knowledge"
  | "compAudit"
  | "activity"
  | "issues"
  | "staff"
  | "tablets"
  | "settings";

interface Props {
  activeTab: OperationsStudioTab;
  badgeCounts?: {
    dashboard?: number;
    context?: number;
    knowledge?: number;
    compAudit?: number;
    activity?: number;
    issues?: number;
    staff?: number;
    tablets?: number;
    settings?: number;
  };
}

const TABS: {
  id: OperationsStudioTab;
  to: string;
  label: string;
  countKey?: keyof NonNullable<Props["badgeCounts"]>;
}[] = [
  { id: "dashboard", to: "/admin/dashboard", label: "📊 Executive Dashboard", countKey: "dashboard" },
  { id: "context", to: "/admin/business-context", label: "🌐 Demand Context", countKey: "context" },
  { id: "knowledge", to: "/admin/knowledge", label: "🧠 SOPs & Sentinel AI", countKey: "knowledge" },
  { id: "compAudit", to: "/admin/comp-audit", label: "⚖️ Comp & Waste Audit", countKey: "compAudit" },
  { id: "activity", to: "/admin/activity", label: "📋 Audit Activity Log", countKey: "activity" },
  { id: "issues", to: "/admin/issues", label: "⚠️ Issue Reports", countKey: "issues" },
  { id: "staff", to: "/admin/staff-access", label: "👥 Staff Access", countKey: "staff" },
  { id: "tablets", to: "/admin/station-tablets", label: "📱 Station Hardware", countKey: "tablets" },
  { id: "settings", to: "/admin/settings", label: "⚙️ System Settings", countKey: "settings" },
];

export default function OperationsStudioTabs({ activeTab, badgeCounts }: Props) {
  const isPhone = useIsMobile(768);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  // A <select> only fires `change` when the value changes. On a sub-route of a tab (AdminVoice
  // is /admin/settings/voice with activeTab="settings") the picker would sit on "settings" and
  // picking System Settings would do nothing, so show a placeholder there instead.
  const activeEntry = TABS.find((t) => t.id === activeTab);
  const onTabRoot = activeEntry?.to === pathname;
  return (
    <div style={styles.container}>
      <div style={{ ...styles.headerRow, marginBottom: isPhone ? 12 : 16 }}>
        <div>
          <div style={styles.badge}>Operations &amp; Intelligence Studio</div>
          <h1 style={{ ...styles.title, fontSize: isPhone ? 18 : 22 }}>Operations, Telemetry &amp; System Studio</h1>
          {!isPhone && (
            <p style={styles.subtitle}>
              Monitor venue revenue KPIs, external demand telemetry, staff training SOPs, comp audits, and system security controls.
            </p>
          )}
        </div>
      </div>

      {/* Phones: nine wrapped tabs filled the first screen, so they collapse to one native
          picker (vertical list, no sideways strip — same call as the v0.3.96 admin menu). */}
      {isPhone ? (
        <label style={styles.pickerLabel}>
          <span style={styles.pickerCaption}>Studio section</span>
          <select
            value={onTabRoot ? activeTab : ""}
            onChange={(e) => {
              const tab = TABS.find((t) => t.id === e.target.value);
              if (tab) navigate(tab.to);
            }}
            style={styles.picker}
          >
            {!onTabRoot && (
              <option value="" disabled hidden>
                {activeEntry ? `${activeEntry.label} ›` : "Choose a section"}
              </option>
            )}
            {TABS.map((tab) => {
              const count = tab.countKey ? badgeCounts?.[tab.countKey] : undefined;
              return (
                <option key={tab.id} value={tab.id}>
                  {typeof count === "number" ? `${tab.label} (${count})` : tab.label}
                </option>
              );
            })}
          </select>
        </label>
      ) : (
        <nav style={styles.tabNav} aria-label="Operations and Intelligence Studio Tabs">
          {TABS.map((tab) => {
            const isActive = tab.id === activeTab;
            const count = tab.countKey ? badgeCounts?.[tab.countKey] : undefined;
            return (
              <NavLink
                key={tab.id}
                to={tab.to}
                style={{
                  ...styles.tabLink,
                  ...(isActive ? styles.tabLinkActive : {}),
                }}
              >
                <span>{tab.label}</span>
                {typeof count === "number" && (
                  <span style={isActive ? styles.countActive : styles.countInactive}>
                    {count}
                  </span>
                )}
              </NavLink>
            );
          })}
        </nav>
      )}
    </div>
  );
}

const styles: Record<string, CSSProperties> = {
  container: {
    marginBottom: 20,
    borderBottom: "1px solid var(--border)",
    paddingBottom: 12,
  },
  headerRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 16,
    flexWrap: "wrap",
    gap: 12,
  },
  badge: {
    display: "inline-block",
    fontSize: 10,
    fontWeight: 800,
    textTransform: "uppercase",
    letterSpacing: 1,
    color: "#2DD4BF",
    background: "rgba(45, 212, 191, 0.12)",
    padding: "3px 8px",
    borderRadius: 4,
    marginBottom: 6,
  },
  title: {
    margin: 0,
    fontSize: 22,
    fontWeight: 700,
    color: "var(--text)",
    letterSpacing: "-0.01em",
  },
  subtitle: {
    margin: "4px 0 0 0",
    fontSize: 13,
    color: "var(--muted)",
  },
  tabNav: {
    display: "flex",
    gap: 8,
    flexWrap: "wrap",
    paddingBottom: 4,
  },
  pickerLabel: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
  },
  pickerCaption: {
    fontSize: 10,
    fontWeight: 800,
    textTransform: "uppercase",
    letterSpacing: 0.6,
    color: "var(--muted)",
  },
  picker: {
    width: "100%",
    minHeight: 44,
    padding: "0 12px",
    borderRadius: 8,
    fontSize: 16, // ≥16px stops iOS Safari zooming the page on focus
    fontWeight: 700,
    color: "var(--text)",
    background: "var(--surface)",
    border: "1px solid var(--gold)",
  },
  tabLink: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "8px 14px",
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 600,
    color: "var(--muted)",
    textDecoration: "none",
    background: "rgba(255, 255, 255, 0.02)",
    border: "1px solid var(--border)",
    transition: "all 0.15s ease",
    whiteSpace: "nowrap",
  },
  tabLinkActive: {
    color: "#0D0B08",
    background: "var(--gold)",
    borderColor: "var(--gold)",
    fontWeight: 700,
  },
  countActive: {
    fontSize: 11,
    background: "rgba(0,0,0,0.18)",
    color: "#0D0B08",
    padding: "1px 6px",
    borderRadius: 10,
    fontWeight: 800,
  },
  countInactive: {
    fontSize: 11,
    background: "rgba(255,255,255,0.06)",
    color: "var(--muted)",
    padding: "1px 6px",
    borderRadius: 10,
    fontWeight: 600,
  },
};
